import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Space } from 'antd';
import { LockOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';

const { Title, Text, Link } = Typography;

interface CustomerLoginProps {
  identifier?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  onForgotPassword?: () => void;
}

const CustomerLogin: React.FC<CustomerLoginProps> = ({
  identifier: initialIdentifier,
  onSuccess,
  onCancel,
  onForgotPassword,
}) => {
  const [form] = Form.useForm();
  const { login } = useCustomerAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const identifierValue = Form.useWatch('identifier', form) || '';
  const isEmailInput = identifierValue.includes('@');

  const handleSubmit = async (values: { identifier: string; password: string }) => {
    setLoading(true);
    setError(null);

    try {
      await login(values.identifier, values.password);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      style={{
        maxWidth: 450,
        margin: '0 auto',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ textAlign: 'center' }}>
          <Title level={3}>Welcome Back!</Title>
          <Text type="secondary">Login to your customer account</Text>
        </div>

        {error && (
          <Alert
            message="Login Failed"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ identifier: initialIdentifier }}
        >
          <Form.Item
            label="Email or Phone Number"
            name="identifier"
            rules={[
              { required: true, message: 'Please enter your email or phone number' },
              { min: 3, message: 'Must be at least 3 characters' },
            ]}
          >
            <Input
              prefix={isEmailInput ? <MailOutlined /> : <PhoneOutlined />}
              placeholder="Email or phone number"
              size="large"
              disabled={!!initialIdentifier}
            />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: 'Please enter your password' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter your password"
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              block
            >
              Login
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center' }}>
          {onForgotPassword && (
            <Link onClick={onForgotPassword} style={{ marginRight: 16 }}>
              Forgot Password?
            </Link>
          )}
          {onCancel && (
            <Link onClick={onCancel}>
              Continue as Guest
            </Link>
          )}
        </div>
      </Space>
    </Card>
  );
};

export default CustomerLogin;
